/**
 * SyncComponent
 *
 * This component is responsible for displaying the synchronization status
 * and available sync services. It interacts with the server state to determine
 * the current status and updates accordingly.
 * ## User Interface
 * - Label: Sync data form source:
 * - MUI Dropdown for selecting sync services
 * - Start Button to start the synchronization process
 * - Label to display log message retrieved via a websocket.
 * 
 * ## Log behavoir
 * List all message form websocket that have message_type SYNC_MESSAGE, SYNC_START or SYNC_ERROR
 * 
 * ## API
 * - Use AdminService.postSync to start a synchronization process
 * - Use AdminService.listSyncServices to retrieve available sync services
 * - Connect to the websocket to receive log messages.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
    Tooltip
} from '@mui/material';
import type { SyncEventRequest, ValidationResponse } from '../api-client/index';
import { useIntl } from 'react-intl';
import { AdminService } from '../api-client/services/AdminService';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import StartIcon from '@mui/icons-material/Start';
import type { ServiceInfo } from '../api-client/index';
import type { Message, MessageType, WebSocketMessage } from '../api/websocket-types';
import SessionContext, { type Session, defaultSession, useSession } from '../SessionContext';

import { useINotify, Severity } from './iNotify';
const SYNC_MESSAGE_TYPES: MessageType[] = ['SYNC_START', 'SYNC_MESSAGE', 'SYNC_ERROR', 'SYNC_COMPLETE'];

const messageColor = (type: MessageType): 'error.main' | 'success.dark' | 'text.primary' => {
    if (type === 'SYNC_ERROR') return 'error.main';
    if (type === 'SYNC_COMPLETE') return 'success.dark';
    return 'text.primary';
};

const SyncComponent: React.FC<{ onStart?: () => void }> = ({ onStart }) => {
    const notify = useINotify();
    const intl = useIntl();
    const { session, setSession } = useSession();
    const [services, setServices] = useState<ServiceInfo[]>([]);
    const [selectedUrl, setSelectedUrl] = useState('');
    const [starting, setStarting] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const socketRef = useRef<WebSocket | null>(null);
    const [complete, setComplete] = useState(false);
    const [error, setError] = useState(false);


    const handleSyncCheck = async (url: string): Promise<boolean> => {
        if (!url) {
            return false;
        }
        const request = { url } as SyncEventRequest;

        const response = await AdminService.postSyncCheck(request);
        const vr: ValidationResponse = response;
        console.log(`Validation response using ${url}`, vr);
        return !vr.valid;
    };


    useEffect(() => {
        AdminService.listSyncServices()
            .then(async (data) => {
                console.log('Loaded sync services:', data);
                if (data?.length) {
                    const sdata = await Promise.all(data.map(async (service) => {
                        const isValid = await handleSyncCheck(service.url);
                        return isValid ? service : null;
                    }));
                    const filteredData = sdata.filter(service => service !== null) as ServiceInfo[];
                    console.log('Filtered sync services:', filteredData);
                    setServices(filteredData ?? []);
                    if (filteredData.length > 0) {
                        setSelectedUrl((current) => current || filteredData[0].url);
                    }
                }
                else {
                    setServices([]);
                }
            })
            .catch((error) => {
                console.error('Failed to load sync services:', error);
                setServices([]);
            });
    }, []);

    useEffect(() => {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const socket = new WebSocket(`${wsProtocol}//${window.location.host}/ws/connect`);
        socketRef.current = socket;

        socket.onmessage = (event) => {
            const wsMessage: WebSocketMessage = JSON.parse(event.data);

            if (SYNC_MESSAGE_TYPES.includes(wsMessage.message_type)) {
                const newMessage: Message = {
                    type: wsMessage.message_type,
                    message: wsMessage.data as string,
                    dttm: wsMessage.dttm,
                };
                setMessages((prev) => [...prev, newMessage]);
                if (wsMessage.message_type === 'SYNC_COMPLETE') {
                    // Sync has completed, you might want to perform additional actions here, e.g., notify the user
                    notify({ message: '__sync_complete__', severity: Severity.SUCCESS });
                    setComplete(true);
                }
                if (wsMessage.message_type === 'SYNC_ERROR') {
                    notify({ message: '__sync_error__', severity: Severity.ERROR });
                    setError(true);
                }
                if (wsMessage.message_type === 'SYNC_START') {
                    notify({ message: '__sync_started__', severity: Severity.INFO });
                }
            }
        };

        socket.onerror = (error) => {
            console.error('Sync WebSocket error:', error);
        };

        return () => {
            socket.close();
        };
    }, []);

    const handleStart = async () => {
        if (!selectedUrl) {
            return;
        }

        try {
            setStarting(true);
            onStart?.();
            console.log('Starting sync for URL:', selectedUrl);
            await AdminService.postSync({ url: selectedUrl });
        } catch (error) {
            console.error('Failed to start sync:', error);
        } finally {
            setStarting(false); 
            const _session = session ?? defaultSession;
            setSession({
                ...(session ?? defaultSession),
                trigger_sync_check: !_session.trigger_sync_check,
            });
            
        }
    };

    // function renderMessage(message: { type: string; text: string; dttm?: string }) {
    //     return `${message.dttm ?? ''} [${message.type}] ${message.message ?? ''}`;
    // }

    function renderMessage(message: { type: string; txt?: string; dttm?: string }) {
        switch (message.type) {
            case 'SYNC_COMPLETE':
                return (
                    <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="600" color="text.primary">
                        {`${message.dttm ?? ''} ${message.txt ?? ''}`}
                    </Typography>
                    <CheckIcon fontSize="large"/>
                    </Stack>
                );
            case 'SYNC_ERROR':
                return (
                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                        {`${message.dttm ?? ''}${message.txt ?? ''}`}
                    </Typography>
                );
            case 'SYNC_START':
                return (
                    <Stack direction="row">
                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                        {`${message.dttm ?? ''} ${message.txt ?? ''}`}
                    </Typography>
                    <StartIcon/>
                    </Stack>
                );
            default:
                return (
                    <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                        {`${message.dttm ?? ''} ${message.txt ?? ''}`}
                    </Typography>
                    <CheckIcon/>
                    </Stack>
                );
        }

    }


    return (
        <Paper sx={{ p: 2, width: '100%' }}>
            <Stack spacing={2}>
                <Tooltip title={intl.formatMessage({ id: '__sync_data_from_source_tooltip__' })}>
                    <Typography variant="subtitle1">{intl.formatMessage({ id: '__sync_data_from_source__' })}</Typography>
                </Tooltip>
                
                <Stack direction="row" spacing={2} alignItems="center">                    
                    <FormControl size="small" sx={{ minWidth: 340 }}>
                        <InputLabel id="sync-service-label">Source</InputLabel>
                        <Select
                            labelId="sync-service-label"
                            label="Source"
                            value={selectedUrl}
                            onChange={(event) => setSelectedUrl(event.target.value)}
                            disabled={services.length === 0}
                        >
                            {services.map((service) => (
                                <MenuItem key={service.uuid ?? service.url} value={service.url}>
                                    {service.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={() => void handleStart()}
                        disabled={!selectedUrl || starting}
                        startIcon={starting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        Start
                    </Button>
                </Stack>

                <Box>
                    {messages.length > 0 && (
                        <Typography variant="body2"  fontWeight="600" sx={{ mb: 1 }}>{intl.formatMessage({ id: '__log_messages__' })}</Typography>
                    )}
                    {/* <Typography variant="body2"  fontWeight="600" sx={{ mb: 1 }}>{intl.formatMessage({ id: '__log_messages__' })}</Typography> */}
                    {messages.length === 0 ? (
                        // <Typography variant="body2" color="text.secondary">No sync messages yet.</Typography>
                        ""
                    ) : (
                        <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
                            {messages.map((message, index) => (
                                <ListItem key={index}>
                                    <ListItemText
                                        primaryTypographyProps={{ sx: { color: messageColor(message.type) } }}
                                        //primary={`  X ${message.dttm ?? ''} [${message.type}] ${message.message ?? ''}`}
                                        primary={renderMessage({type: message.type, txt: message.message, dttm: message.dttm})}

                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                    {complete ? (
                        <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="600" color="text.primary">{intl.formatMessage({ id: '__sync_complete__' })}</Typography>
                        <CheckIcon />
                        </Box>
                    ) : null}
                    {error ? (
                        <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="error">{intl.formatMessage({ id: '__sync_error__' })}</Typography>
                        <ErrorIcon />
                        </Box>
                    ) : null}
                </Box>
            </Stack>
        </Paper>
    );
};

export default SyncComponent;

